import { Button, Notification, SegmentedButton } from '@smartgift/frontend-toolkit';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { AppLoader } from '@hero/atoms';
import { MissingRecipientInfoModal } from '@hero/components/modals';
import { AUTOMATIONS_TAB, OCCASION_TYPES } from '@hero/constants';
import { useGetTagBasedAutomationRecipient } from '@hero/hooks';
import { PageLayout } from '@hero/layouts';
import { ROUTE_PATHS } from '@hero/routes';
import { useGetAutomation, useUpdateAutomation } from '@hero/services/order-service';

import { AutomationAnalytics } from './AutomationAnalytics';
import { AutomationGiftActivity } from './AutomationGiftActivity';
import { OrderSummary } from './OrderSummary';
import { AutomationRecipients } from './Recipients';
import { AutomationThankYouNotesTable } from './ThankYouNotes';

function AutomationDetail() {
  const { t } = useTranslation();
  const { code } = useParams();
  const navigate = useNavigate();
  const { data: automation, isSuccess: isAutomationSuccess } = useGetAutomation({ code });

  const hash = decodeURIComponent(location.hash.slice(1));
  const defaultActiveTab =
    Object.values(AUTOMATIONS_TAB).find(o => o === hash) || AUTOMATIONS_TAB.summary;
  const [activeTab, setActiveTab] = useState(defaultActiveTab);
  const [isMissingInfoModalOpen, setIsMissingInfoModalOpen] = useState(false);

  const { missingInfoContacts } = useGetTagBasedAutomationRecipient(automation);

  const { mutateAsync: updateAutomation } = useUpdateAutomation();

  const automationOptions = [
    {
      value: AUTOMATIONS_TAB.summary,
      label: t('summary'),
      children: <OrderSummary automation={automation} setActiveTab={setActiveTab} />
    },
    {
      value: AUTOMATIONS_TAB.analytics,
      label: t('analytics'),
      children: <AutomationAnalytics automation={automation} setActiveTab={setActiveTab} />
    },
    {
      value: AUTOMATIONS_TAB.recipients,
      label: t('recipients'),
      children: <AutomationRecipients automation={automation} />
    },
    {
      value: AUTOMATIONS_TAB.giftActivity,
      label: t('giftActivity'),
      children: <AutomationGiftActivity automation={automation} />
    },
    {
      value: AUTOMATIONS_TAB.thankYou,
      label: t('tyNotes'),
      children: <AutomationThankYouNotesTable automation={automation} />
    }
  ];

  if (!isAutomationSuccess) return <AppLoader />;

  return (
    <PageLayout
      header={{
        title: t('typeNameDetails', { name: t('automation') }),
        backButton: {
          onClick: () => navigate(ROUTE_PATHS.MY_AUTOMATION_LIST)
        }
      }}
    >
      {missingInfoContacts?.length > 0 && (
        <Notification
          status="warning"
          message={
            <>
              <Trans
                i18nKey="automationTypeRecipientMissingInfoNotification"
                components={{
                  b: <b />,
                  button: (
                    <Button
                      variant="link"
                      color="secondary"
                      onClick={() => setIsMissingInfoModalOpen(true)}
                    />
                  )
                }}
                values={{
                  type: OCCASION_TYPES[automation.type],
                  amount: missingInfoContacts?.length
                }}
              />
            </>
          }
          isClosable
          style={{ marginBottom: 24 }}
        />
      )}
      <SegmentedButton
        isPage
        activeTab={activeTab}
        withLine
        onChange={setActiveTab}
        options={automationOptions}
      />
      {isMissingInfoModalOpen && automation && (
        <MissingRecipientInfoModal
          isOpen={isMissingInfoModalOpen}
          onRequestClose={setIsMissingInfoModalOpen}
          contacts={missingInfoContacts}
          occasionType={automation?.type}
          disabledColumns={[
            automation?.type === 'birthday' ? 'anniversary' : 'birthday',
            automation?.contacts?.tags?.length > 0 && 'actions'
          ]}
          deleteContact={id => {
            updateAutomation({
              params: {
                code
              },
              body: {
                contacts: {
                  ids: automation?.contacts?.ids?.filter(contact => contact.id !== id)
                }
              }
            });
          }}
        />
      )}
    </PageLayout>
  );
}

export { AutomationDetail };
